import {maybe} from '@freckle/maybe'
import type {LinksT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {parseLinkHeader} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {fancyClient, extractDataAndHeaders} from '@freckle/educator-entities/ts/openapi-clients'
import type {CourseTableRowT} from '@freckle/school/ts/manage-teachers/models/course-table-row'
import {parseCourseTableRows} from '@freckle/school/ts/manage-teachers/models/course-table-row'
import type {TableParamsT} from './../components/api-controlled-table/index'
import type {CourseTabFilteringT} from './../components/course-tab'
import {providersOrNoneToQueryFilter} from './filters'

export function fetchCourseTableRows(
  schoolIds: Array<number>,
  tableParams: TableParamsT<CourseTabFilteringT>
): Promise<{
  courseTableRows: Array<CourseTableRowT>
  links: LinksT
  totalCount: number
}> {
  const {additionalFilters} = tableParams

  const additionalFiltersParams = buildAdditionalFilterParams(additionalFilters)

  const query = {
    'schools.id': schoolIds,
    position: tableParams.position ?? undefined,
    limit: tableParams.limit ?? undefined,
    ...additionalFiltersParams
  }

  return fancyClient
    .GET('/3/courses', {
      params: {query}
    })
    .then(extractDataAndHeaders)
    .then(({data, headers}) => {
      const linkHeader = headers.get('Link')
      const links = parseLinkHeader(linkHeader)

      const totalCountHeader = headers.get('X-Total-Count')
      const totalCount = totalCountHeader !== null ? parseInt(totalCountHeader, 10) : 0

      const courseTableRows = parseCourseTableRows(data)
      return {
        courseTableRows,
        links,
        totalCount
      }
    })
}

function buildAdditionalFilterParams(
  additionalFilters?: CourseTabFilteringT | null
): Record<string, Record<string, unknown>> {
  const sisIdFilterParam = maybe(
    () => ({}),
    exists => ({
      [`sis-id[${exists ? 'notin' : 'in'}]`]: 'none'
    }),
    additionalFilters?.sisIdExists
  )
  const studentIdFilterParam = maybe(
    () => ({}),
    x => ({'students.id': x.id}),
    additionalFilters?.student
  )
  const courseNameFilterParam = maybe(
    () => ({}),
    name => ({name}),
    additionalFilters?.courseName
  )
  const teacherNameFilterParam = maybe(
    () => ({}),
    name => ({'teachers.name': name}),
    additionalFilters?.teacherName
  )
  const studentGradeFilterParam = maybe(
    () => ({}),
    grade => ({'students.grade': grade}),
    additionalFilters?.studentGrade
  )

  const identityProviderFilterParam = providersOrNoneToQueryFilter(
    additionalFilters?.identityProviders ?? []
  )

  return {
    ...sisIdFilterParam,
    ...studentIdFilterParam,
    ...courseNameFilterParam,
    ...teacherNameFilterParam,
    ...studentGradeFilterParam,
    ...identityProviderFilterParam
  }
}
